import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getMachine, getServiceReports, PublicState } from "../../publicSlice";
import { dt } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";
import accountContext from "../../../account/accountContext";
import { refreshAccount } from "../../../account/accountSlice";
import { buildAccountSignInWithReturn } from "../../../account/routes";
import KioskTile from "./KioskTile";
import MachineContextHeader from "./MachineContextHeader";
import KioskDashboardSkeleton from "./KioskDashboardSkeleton";
import KioskMidasChat from "./KioskMidasChat";
import type { KioskMachineData } from "./KioskMidasChat";
import { useLocation } from "react-router-dom";

const KioskDashboard: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const { machine, serviceReports, loading } = useAppSelector<PublicState>(
    (state: any) => state.public
  );
  const [midasOpen, setMidasOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getMachine(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (machine?.internalNumber) {
      dispatch(
        getServiceReports({
          machineNumber: machine.internalNumber,
          query: { statusCodes: ["InProgress", "Solved"] },
        })
      );
    }
  }, [machine?.internalNumber, dispatch]);

  const openServiceReportsCount = machine?.openServiceReportsCount || 0;

  const handleLogout = () => {
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    accountContext.logout();
    dispatch(refreshAccount(null));
    navigate(buildAccountSignInWithReturn(currentUrl));
  };

  if (loading && !machine) {
    return (
      <Box sx={{ padding: "16px" }}>
        <KioskDashboardSkeleton />
      </Box>
    );
  }

  const tiles = [
    {
      icon: <ReportProblemOutlined />,
      label: translate("kioskReportFault"),
      accentColor: "#C0392B",
      tinted: true,
      tintBg: "#F9EBEA",
      onClick: () => navigate(`/public/service-reports/create?machine=${machine?.internalNumber}`),
    },
    {
      icon: <AssignmentOutlined />,
      label: translate("kioskOpenServices"),
      accentColor: "#F39C12",
      badge: openServiceReportsCount,
      onClick: () =>
        navigate(`/public/machines/${machine?.internalNumber}/service-reports/open`),
    },
    {
      icon: <MenuBookOutlined />,
      label: translate("kioskMachineManuals"),
      accentColor: "#8E44AD",
      onClick: () =>
        navigate(`/public/documents/${machine?.internalNumber}`),
    },
    {
      icon: <InfoOutlined />,
      label: translate("kioskMachineDetails"),
      accentColor: "#1ABC9C",
      onClick: () =>
        navigate(`/public/machines/${machine?.internalNumber}/details`),
    },
    {
      icon: <BusinessOutlined />,
      label: translate("kioskCompanyDetails"),
      accentColor: dt.blueHover,
      onClick: () =>
        navigate(`/public/machines/${machine?.internalNumber}/company`),
    },
    {
      icon: <HistoryOutlined />,
      label: translate("kioskServiceArchive"),
      accentColor: "#6B7280",
      onClick: () =>
        navigate(`/public/machines/${machine?.internalNumber}/service-reports/archive`),
    },
    {
      icon: <LogoutOutlined />,
      label: translate("kioskLogout"),
      accentColor: "#9CA3AF",
      onClick: handleLogout,
    },
  ];

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        padding: "16px",
        paddingBottom: "32px",
      }}
    >
      <MachineContextHeader machine={machine} />

      {/* Ask MIDAS button — opens full-screen chat overlay (M7-B2) */}
      <Box
        onClick={() => setMidasOpen(true)}
        sx={{
          mt: 2,
          padding: "16px 20px",
          borderRadius: dt.radius,
          background: "linear-gradient(135deg, #D4A017, #F5C842, #D4A017)",
          boxShadow: "0 2px 8px rgba(212, 160, 23, 0.3)",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          cursor: "pointer",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
          transition: "transform 150ms ease, box-shadow 150ms ease",
          animation: "tileEnter 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
          "&:active": { transform: "scale(0.98)" },
          "&:hover": {
            boxShadow: "0 4px 16px rgba(212, 160, 23, 0.4)",
          },
        }}
      >
        <AutoAwesomeRounded sx={{ fontSize: "28px", color: "#FFFFFF" }} />
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: "#FFFFFF",
              letterSpacing: "0.5px",
            }}
          >
            {translate("kioskAskMidas")}
          </Box>
        </Box>
        <AutoAwesomeRounded
          sx={{ fontSize: "20px", color: "rgba(255,255,255,0.6)" }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "16px",
          mt: 2,
        }}
      >
        {tiles.map((tile, index) => (
          <KioskTile
            key={index}
            icon={tile.icon}
            label={tile.label}
            accentColor={tile.accentColor}
            badge={tile.badge}
            tinted={tile.tinted}
            tintBg={tile.tintBg}
            onClick={tile.onClick}
            index={index}
          />
        ))}
      </Box>

      {/* MIDAS chat overlay */}
      {midasOpen && machine && (
        <KioskMidasChat
          machine={{
            machineId: machine.id,
            model: machine.model,
            serialNumber: machine.serialNumber,
            controllerSystem: machine.system,
            manufacturerName: machine.manufacturer?.name,
          } as KioskMachineData}
          onClose={() => setMidasOpen(false)}
        />
      )}
    </Box>
  );
};

export default KioskDashboard;
